import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ClipboardList, Plus, Trash2, CheckCircle2, PackageOpen, Receipt, Printer } from "lucide-react";
import { useNavigate } from "react-router-dom";

const STATUS = { draft:"مسودة", approved:"معتمد", partial:"استلام جزئي", received:"مستلم بالكامل", invoiced:"تم التحويل لفاتورة" };
const STATUS_COLOR = { draft:"bg-slate-100 text-slate-800", approved:"bg-blue-100 text-blue-800", partial:"bg-amber-100 text-amber-800", received:"bg-emerald-100 text-emerald-800", invoiced:"bg-violet-100 text-violet-800" };

export default function PurchaseOrders() {
  const nav = useNavigate();
  const [pos, setPos] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ supplier_id:"", supplier_name:"", expected_date: new Date().toISOString().slice(0,10), warehouse_id:"", currency:"SYP", exchange_rate:1, notes:"", lines:[{ product_id:"", product_name:"", quantity:1, price:0 }] });

  const load = async () => {
    try {
      const [p, pr, s, w] = await Promise.all([
        api.get("/purchase-orders"),
        api.get("/products").catch(()=>({data:[]})),
        api.get("/suppliers").catch(()=>({data:[]})),
        api.get("/warehouses").catch(()=>({data:[]})),
      ]);
      setPos(p.data); setProducts(pr.data); setSuppliers(s.data); setWarehouses(w.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ supplier_id:"", supplier_name:"", expected_date: new Date().toISOString().slice(0,10), warehouse_id:"", currency:"SYP", exchange_rate:1, notes:"", lines:[{ product_id:"", product_name:"", quantity:1, price:0 }] }); setOpen(true); };
  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, { product_id:"", product_name:"", quantity:1, price:0 }] }));
  const setLine = (i, patch) => setForm(f => ({ ...f, lines: f.lines.map((l, idx) => idx===i ? {...l, ...patch} : l) }));
  const rmLine = (i) => setForm(f => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  const save = async () => {
    const lines = form.lines.filter(l => l.product_id && +l.quantity > 0);
    if (!form.supplier_name.trim() || !lines.length) return toast.error("مورد وبند واحد على الأقل مطلوبان");
    try {
      await api.post("/purchase-orders", { ...form, warehouse_id: form.warehouse_id || null, supplier_id: form.supplier_id || null, lines: lines.map(l => ({ ...l, quantity: +l.quantity, price: +l.price })) });
      toast.success("تم إنشاء أمر الشراء");
      setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const approve = async (po) => {
    try { await api.put(`/purchase-orders/${po.id}/approve`); toast.success("اعتُمد أمر الشراء"); load(); } catch (e) { toast.error(formatErr(e)); }
  };
  const receiveFull = async (po) => {
    if (!confirm(`استلام كامل الكميات لأمر #${po.number}?`)) return;
    try {
      const lines = po.lines.map(l => ({ product_id: l.product_id, quantity: Math.max(0, (l.quantity || 0) - (l.received || 0)) })).filter(l => l.quantity > 0);
      if (!lines.length) return toast.info("كل البضاعة مستلمة سابقاً");
      await api.post(`/purchase-orders/${po.id}/receive`, { po_id: po.id, lines, notes:"استلام كامل" });
      toast.success("تم تسجيل الاستلام"); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const convert = async (po) => {
    if (!confirm(`تحويل أمر #${po.number} إلى فاتورة شراء؟`)) return;
    try {
      const { data } = await api.post(`/purchase-orders/${po.id}/convert-to-invoice`);
      toast.success(`تم إنشاء فاتورة الشراء #${data.invoice_number}`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const del = async (po) => {
    if (!confirm(`حذف أمر الشراء #${po.number}?`)) return;
    try { await api.delete(`/purchase-orders/${po.id}`); toast.success("تم الحذف"); load(); } catch (e) { toast.error(formatErr(e)); }
  };

  const printPO = (po) => {
    const w = window.open("", "_blank");
    const rowsHtml = po.lines.map((l, i) => `<tr>
      <td class="c">${i + 1}</td>
      <td>${l.product_name}</td>
      <td class="e">${l.quantity}</td>
      <td class="e">${l.price.toLocaleString("ar-EG")}</td>
      <td class="e">${(l.quantity * l.price).toLocaleString("ar-EG")}</td>
      <td class="e" style="color:#059669">${(l.received || 0).toLocaleString("ar-EG")}</td>
    </tr>`).join("");
    w.document.write(`<html dir="rtl"><head><title>أمر شراء #${po.number}</title>
      <style>
        body{font-family:Cairo,Arial;padding:20px;color:#000}
        .head{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:20px;padding-bottom:12px;border-bottom:3px double #1E3A8A}
        h1{color:#1E3A8A;margin:0;font-size:24px}
        .badge{display:inline-block;padding:4px 12px;border-radius:20px;background:#DBEAFE;color:#1E3A8A;font-size:12px;font-weight:bold}
        table{width:100%;border-collapse:collapse;margin-top:8px}
        th,td{border:1px solid #E2E8F0;padding:8px;font-size:12px;text-align:start}
        th{background:#F1F5F9}
        .c{text-align:center}
        .e{text-align:end;font-variant-numeric:tabular-nums}
        .total{background:#DCFCE7;font-weight:bold;font-size:14px}
        .footer{margin-top:32px;display:flex;justify-content:space-between;font-size:12px}
        .sig{border-top:1px solid #000;padding-top:6px;text-align:center;width:200px}
        @page { size: A4; margin: 15mm; }
      </style></head><body>
      <div class="head">
        <div>
          <h1>أمر شراء</h1>
          <div style="font-size:14px;color:#64748B;margin-top:2px">Purchase Order #${po.number}</div>
          <div style="margin-top:6px" class="badge">${po.status === "draft" ? "مسودة" : po.status === "approved" ? "معتمد" : po.status === "partial" ? "استلام جزئي" : po.status === "received" ? "مستلم" : "مُحوَّل"}</div>
        </div>
        <div style="text-align:end;font-size:12px">
          <div><b>التاريخ:</b> ${new Date(po.created_at).toLocaleDateString("ar-EG")}</div>
          ${po.expected_date ? `<div><b>تاريخ التسليم:</b> ${po.expected_date}</div>` : ""}
          <div><b>العملة:</b> ${po.currency}</div>
        </div>
      </div>
      <div style="background:#F8FAFC;padding:12px;border-radius:6px;margin-bottom:12px">
        <div style="font-size:11px;color:#64748B">المورد</div>
        <div style="font-size:16px;font-weight:bold;color:#1E3A8A;margin-top:2px">${po.supplier_name}</div>
      </div>
      <table>
        <thead><tr><th class="c">#</th><th>الصنف</th><th class="e">الكمية المطلوبة</th><th class="e">السعر</th><th class="e">الإجمالي</th><th class="e">المستلم</th></tr></thead>
        <tbody>${rowsHtml}</tbody>
        <tfoot><tr class="total">
          <td colspan="4" class="e">الإجمالي</td>
          <td class="e">${po.total.toLocaleString("ar-EG")} ${po.currency}</td>
          <td></td>
        </tr></tfoot>
      </table>
      ${po.notes ? `<div style="margin-top:12px;padding:8px;background:#FEF3C7;border-radius:4px;font-size:12px"><b>ملاحظات:</b> ${po.notes}</div>` : ""}
      <div class="footer">
        <div class="sig">توقيع أمين المستودع</div>
        <div class="sig">توقيع المحاسب</div>
        <div class="sig">اعتماد المدير العام</div>
      </div>
      <script>window.onload=()=>setTimeout(()=>window.print(),300)</script>
      </body></html>`);
    w.document.close();
  };

  return (
    <div className="space-y-6" data-testid="purchase-orders-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-blue-600" /> أوامر الشراء
          </h1>
          <p className="text-sm text-muted-foreground mt-1">إنشاء PO → اعتماد → استلام (GRN) → تحويل تلقائي إلى فاتورة شراء</p>
        </div>
        <Button onClick={openNew} className="gap-1" data-testid="add-po-btn"><Plus className="w-4 h-4" /> أمر شراء جديد</Button>
      </div>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="text-start">رقم</TableHead><TableHead className="text-start">المورد</TableHead>
            <TableHead className="text-start">التاريخ</TableHead><TableHead className="text-end">الإجمالي</TableHead>
            <TableHead className="text-start">الحالة</TableHead><TableHead className="text-end">إجراءات</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {pos.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">لا توجد أوامر شراء</TableCell></TableRow>}
            {pos.map(po => (
              <TableRow key={po.id} data-testid={`po-row-${po.id}`}>
                <TableCell className="font-mono">#{po.number}</TableCell>
                <TableCell className="font-semibold">{po.supplier_name}</TableCell>
                <TableCell className="text-xs">{new Date(po.created_at).toLocaleDateString("ar-EG")}</TableCell>
                <TableCell className="text-end tabular-nums font-bold">{po.total?.toLocaleString("ar-EG")} {po.currency}</TableCell>
                <TableCell><span className={`text-[10px] px-1.5 py-0.5 rounded ${STATUS_COLOR[po.status] || ""}`}>{STATUS[po.status] || po.status}</span></TableCell>
                <TableCell className="text-end">
                  {po.status === "draft" && <Button size="sm" variant="ghost" onClick={()=>approve(po)} className="text-blue-600" data-testid={`approve-po-${po.id}`} title="اعتماد"><CheckCircle2 className="w-4 h-4" /></Button>}
                  {(po.status === "approved" || po.status === "partial") && <Button size="sm" variant="ghost" onClick={()=>receiveFull(po)} className="text-emerald-600" data-testid={`receive-po-${po.id}`} title="استلام كامل"><PackageOpen className="w-4 h-4" /></Button>}
                  {(po.status === "received" || po.status === "partial") && <Button size="sm" variant="ghost" onClick={()=>convert(po)} className="text-violet-600" data-testid={`convert-po-${po.id}`} title="تحويل لفاتورة"><Receipt className="w-4 h-4" /></Button>}
                  {po.status === "invoiced" && po.converted_invoice_id && <Button size="sm" variant="ghost" onClick={()=>nav(`/invoices/${po.converted_invoice_id}/edit`)} className="text-primary text-xs">عرض الفاتورة</Button>}
                  <Button size="sm" variant="ghost" onClick={()=>printPO(po)} title="طباعة" data-testid={`print-po-${po.id}`}><Printer className="w-3.5 h-3.5" /></Button>
                  {po.status !== "invoiced" && <Button size="sm" variant="ghost" onClick={()=>del(po)} className="text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="po-dialog">
          <DialogHeader><DialogTitle>أمر شراء جديد</DialogTitle></DialogHeader>
          <div className="grid grid-cols-4 gap-3">
            <div className="col-span-2"><Label>المورد</Label>
              <Select value={form.supplier_id || undefined} onValueChange={(v)=>{const s=suppliers.find(x=>x.id===v); setForm({...form, supplier_id:v, supplier_name: s?.name || ""});}}>
                <SelectTrigger data-testid="po-supplier"><SelectValue placeholder="اختر مورداً" /></SelectTrigger>
                <SelectContent>{suppliers.map(s=><SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>المستودع</Label>
              <Select value={form.warehouse_id || "__none__"} onValueChange={(v)=>setForm({...form, warehouse_id: v==="__none__"?"":v})}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="__none__">الافتراضي</SelectItem>{warehouses.map(w=><SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>العملة</Label>
              <Select value={form.currency} onValueChange={(v)=>setForm({...form, currency:v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="SYP">SYP</SelectItem><SelectItem value="TRY">TRY</SelectItem><SelectItem value="USD">USD</SelectItem><SelectItem value="EUR">EUR</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="col-span-4 space-y-1.5 border border-border rounded-md p-2 max-h-64 overflow-auto">
              <div className="flex items-center justify-between">
                <Label className="text-xs">البنود</Label>
                <Button size="sm" variant="outline" onClick={addLine} className="h-6 text-[10px]" data-testid="po-add-line">+ بند</Button>
              </div>
              {form.lines.map((l, i) => (
                <div key={i} className="grid grid-cols-[1fr_80px_80px_40px] gap-1">
                  <Select value={l.product_id || undefined} onValueChange={(v)=>{ const p=products.find(x=>x.id===v); setLine(i, { product_id:v, product_name: p?.name || "", price: p?.cost_price || l.price }); }}>
                    <SelectTrigger className="h-8" data-testid={`po-prod-${i}`}><SelectValue placeholder="اختر" /></SelectTrigger>
                    <SelectContent>{products.slice(0,300).map(p=><SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" step="0.01" value={l.quantity} onChange={(e)=>setLine(i,{quantity:e.target.value})} className="h-8" data-testid={`po-qty-${i}`} placeholder="كمية" />
                  <Input type="number" step="0.01" value={l.price} onChange={(e)=>setLine(i,{price:e.target.value})} className="h-8" data-testid={`po-price-${i}`} placeholder="سعر" />
                  <Button size="sm" variant="ghost" onClick={()=>rmLine(i)} className="h-8"><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>
                </div>
              ))}
            </div>
            <div className="col-span-4"><Label>ملاحظات</Label><Input value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>إلغاء</Button><Button onClick={save} data-testid="po-save-btn">حفظ</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
